package co.uk.paxes.pinpoint.model

/**
 * Represents one of the 9 sections on the PINPOINT board
 * Each section can be up to 4x4 to support the full 10x10 board
 */
@kotlinx.serialization.Serializable
data class Section(
    val sectionRow: Int,
    val sectionCol: Int,
    val pins: Array<Array<Pin?>> = Array(4) { Array(4) { null } }
) {
    init {
        require(sectionRow in 0..2) { "Section row must be 0-2" }
        require(sectionCol in 0..2) { "Section col must be 0-2" }
    }
    
    fun getPinAt(localRow: Int, localCol: Int): Pin? {
        require(localRow in 0..3 && localCol in 0..3) { "Local position must be 0-3" }
        return pins[localRow][localCol]
    }
    
    fun isEmpty(localRow: Int, localCol: Int): Boolean = getPinAt(localRow, localCol) == null
    
    fun placePinAt(localRow: Int, localCol: Int, pin: Pin): Section {
        require(isEmpty(localRow, localCol)) { "Position ($localRow, $localCol) in section ($sectionRow, $sectionCol) is occupied" }
        val newPins = Array(4) { row ->
            Array(4) { col ->
                if (row == localRow && col == localCol) pin else pins[row][col]
            }
        }
        return copy(pins = newPins)
    }
    
    fun replacePinAt(localRow: Int, localCol: Int, pin: Pin): Section {
        require(localRow in 0..3 && localCol in 0..3) { "Local position must be 0-3" }
        val newPins = Array(4) { row ->
            Array(4) { col ->
                if (row == localRow && col == localCol) pin else pins[row][col]
            }
        }
        return copy(pins = newPins)
    }
    
    /**
     * Remove a pin from the section at the specified local position.
     * Used for undo functionality.
     */
    fun removePinAt(localRow: Int, localCol: Int): Section {
        require(localRow in 0..3 && localCol in 0..3) { "Local position must be 0-3" }
        val newPins = Array(4) { row ->
            Array(4) { col ->
                if (row == localRow && col == localCol) null else pins[row][col]
            }
        }
        return copy(pins = newPins)
    }
    
    fun getOccupiedPositions(): List<Pair<Int, Int>> {
        val occupied = mutableListOf<Pair<Int, Int>>()
        for (row in 0..3) {
            for (col in 0..3) {
                if (pins[row][col] != null) {
                    occupied.add(Pair(row, col))
                }
            }
        }
        return occupied
    }
    
    fun isFull(): Boolean = getOccupiedPositions().size == 16
    
    fun getPinsOfColor(color: PinColor): List<Pair<Int, Int>> {
        val positions = mutableListOf<Pair<Int, Int>>()
        for (row in 0..3) {
            for (col in 0..3) {
                val pin = pins[row][col]
                if (pin?.getEffectiveColor() == color) {
                    positions.add(Pair(row, col))
                }
            }
        }
        return positions
    }
    
    override fun equals(other: Any?): Boolean {
        if (this === other) return true
        if (other == null || this::class != other::class) return false
        other as Section
        return sectionRow == other.sectionRow && 
               sectionCol == other.sectionCol && 
               pins.contentDeepEquals(other.pins)
    }
    
    override fun hashCode(): Int {
        var result = sectionRow
        result = 31 * result + sectionCol
        result = 31 * result + pins.contentDeepHashCode()
        return result
    }
}
